import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { exists } from '@jack-henry/frontend-utils/functions';
import { AccountsService } from '@treasury/domain/accounts';
import { type CheckImage } from '@treasury/domain/accounts/types/check-image.entity';
import { TmBaseComponent } from '@treasury/presentation';
import '@treasury/presentation/components/tm-bottom-sheet';
import { GridActiveItemChangedEvent } from '@vaadin/grid';
import { css, html, nothing } from 'lit';
import { columnBodyRenderer, gridRowDetailsRenderer } from 'lit-vaadin-helpers';
import { customElement, property, state } from 'lit/decorators.js';
import '../../components/jhd-data-field';
import { ResearchedTransactionVm } from '../data/researched-transaction-view-model';

export const tagName = 'account-research-transactions-detail-sheet';
@customElement(tagName)
export class AccountResearchTransactionsDetailSheet extends TmBaseComponent {
    @property({ type: Object })
    public transaction?: ResearchedTransactionVm;

    @property({ type: Boolean })
    public open = false;

    @InjectProperty()
    declare private readonly accountsService: AccountsService;

    @state()
    private checkImages: CheckImage[] = [];

    @state()
    private depositCheckImage?: CheckImage;

    @state()
    private loadingCheckImages = false;

    @state()
    private loadingDepositImages = false;

    @state()
    private depositCheckOpened: CheckImage[] = [];

    protected async updated(changedProps: Map<string, boolean>) {
        if (changedProps.has('transaction') && this.transaction) {
            this.checkImages = [];
            this.depositCheckImage = undefined;
            this.depositCheckOpened = [];

            if (this.transaction.checkImageNumber) {
                await this.fetchCheckImages();
            }
        }
    }

    protected render() {
        return html`<tm-bottom-sheet
            .open=${this.open}
            @close=${() => {
                this.open = false;
                this.dispatchEvent(new CustomEvent('close'));
            }}
        >
            <div slot="header-center">Transaction Detail</div>
            <div slot="header-right"></div>
            ${this.renderDetails()}
        </tm-bottom-sheet>`;
    }

    private renderDetails() {
        if (!this.transaction) {
            return nothing;
        }
        return html`<tm-section id="transaction-details" class="px-6 py-2">
            <jhd-data-field
                label="Account Name"
                .value=${this.transaction.accountName}
            ></jhd-data-field>
            <jhd-data-field
                label="Account Number"
                .value=${this.transaction.accountNumber}
            ></jhd-data-field>
            <jhd-data-field
                label="Account Type"
                .value=${this.transaction.accountType}
            ></jhd-data-field>
            <jhd-data-field
                label="Description"
                .value=${this.transaction.description}
            ></jhd-data-field>
            <jhd-data-field
                label="Amount"
                .value=${this.transaction.displayAmount}
            ></jhd-data-field>
            <jhd-data-field
                label="Transaction Type"
                .value=${this.transaction.transactionType}
            ></jhd-data-field>
            <jhd-data-field
                label="Transaction ID"
                .value=${this.transaction.transactionId}
            ></jhd-data-field>
            <jhd-data-field
                label="Check Number"
                .value=${this.transaction.checkNumber}
            ></jhd-data-field>
            <jhd-data-field label="Code" .value=${this.transaction.displayCode}></jhd-data-field>
            <jhd-data-field label="Date" .value=${this.transaction.displayDate}></jhd-data-field>
            ${this.renderCheckImages()} ${this.renderDepositChecksTable()}
        </tm-section>`;
    }

    private renderCheckImages() {
        if (!this.transaction) {
            return nothing;
        }
        if (!this.transaction.source.checkImageNumber) {
            return nothing;
        }
        if (this.loadingCheckImages) {
            return html`<tm-loader card></tm-loader>`;
        }
        if (!this.checkImages.length) {
            return nothing;
        }
        return html`<img
                alt="Check Front"
                src=${`data:image/jpg;base64,${this.checkImages[0].frontImage}`}
            />
            <img
                alt="Check Back"
                src=${`data:image/jpg;base64,${this.checkImages[0].backImage}`}
            /> `;
    }

    private renderDepositCheckNumberColumn(checkImage: CheckImage) {
        const index = this.checkImages.indexOf(checkImage) + 1;
        const { length } = this.checkImages;
        return html`<span class="text-sm text-header">Check Image ${index} of ${length}</span>`;
    }

    private renderDepositCheckImages() {
        if (this.loadingDepositImages) {
            return html`<tm-loader card></tm-loader>`;
        }
        if (!this.depositCheckImage) {
            return html`<div>No Check Images Available</div>`;
        }
        return html`<div>
            <img
                alt="Check Front"
                src=${`data:image/jpg;base64,${this.depositCheckImage.frontImage}`}
            />
            <img
                alt="Check Back"
                src=${`data:image/jpg;base64,${this.depositCheckImage.backImage}`}
            />
        </div>`;
    }

    private renderDepositChecksTable() {
        if (this.loadingCheckImages) {
            return nothing;
        }
        if (!this.checkImages.length) {
            return nothing;
        }
        return html`<div class="deposit-checks">
            <div class="check-images pt-3 px-3 border-t border-dashed border-container-page">
                <vaadin-grid
                    .items=${this.checkImages}
                    .detailsOpenedItems=${this.depositCheckOpened}
                    @active-item-changed=${(e: GridActiveItemChangedEvent<CheckImage>) => {
                        if (exists(e.detail.value)) {
                            this.updateOpenedDepositCheck(e.detail.value);
                        }
                    }}
                    ${gridRowDetailsRenderer<CheckImage>(() => this.renderDepositCheckImages())}
                    ><vaadin-grid-sort-column
                        auto-width
                        ${columnBodyRenderer(this.renderDepositCheckNumberColumn, [])}
                    ></vaadin-grid-sort-column>
                </vaadin-grid>
            </div>
        </div>`;
    }

    private async updateOpenedDepositCheck(check: CheckImage) {
        if (!check) {
            return;
        }
        const selectedCheck = check;
        if (selectedCheck.id === this.depositCheckOpened[0]?.id) {
            this.depositCheckOpened = [];
        } else {
            this.loadingDepositImages = true;
            const depositCheckImage = await this.accountsService.getCheckImages(
                selectedCheck.id,
                'check'
            );
            // eslint-disable-next-line prefer-destructuring
            this.depositCheckImage = depositCheckImage[0];
            this.depositCheckOpened = [selectedCheck];
            this.loadingDepositImages = false;
        }
    }

    private async fetchCheckImages() {
        try {
            this.loadingCheckImages = true;
            this.checkImages = await this.accountsService.getCheckImages(
                this.transaction?.source?.checkImageNumber ?? '',
                this.transaction?.source?.isDebit ? 'check' : 'deposit'
            );
        } finally {
            this.loadingCheckImages = false;
        }
    }

    public static get styles() {
        return [
            css`
                :host {
                    background: var(--container-primary);
                }
                path {
                    fill: var(--brand-primary);
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: AccountResearchTransactionsDetailSheet;
    }
}
